import React, { useCallback } from 'react'
import { getSpaceNameFromCache } from '@components/Analytics/getSpaceNameFromCache'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { SECOND_MS } from 'data/constants'
import { Analytics } from 'hooks/useAnalytics'
import { StakableOperator } from 'hooks/useOperatorData'
import {
    SpaceIdFromSpaceAddress,
    TSigner,
    useAddSpaceDelegationTransaction,
} from 'use-towns-client'

type Params = {
    spaceAddress: `0x${string}` | undefined
    operatorAddress: `0x${string}` | undefined
    operatorMetadata: StakableOperator | undefined
}

const defaultOperatorMetadata: StakableOperator = {
    name: 'Operator',
    image: '',
    address: '0x0000000000000000000000000000000000000000',
    metrics: {
        http20: 0,
        grpc: 0,
        uptime_percentage: 0,
        grpc_start_time: '',
    },
    nodes: [],
    commissionPercentage: 0,
    estimatedApr: 0,
    isActive: false,
}

export const useDelegateToNodeOperator = (params: Params) => {
    const { spaceAddress, operatorAddress, operatorMetadata = defaultOperatorMetadata } = params

    const { addSpaceDelegation } = useAddSpaceDelegationTransaction()

    const delegateToNode = useCallback(
        async (getSigner: () => Promise<TSigner | undefined>) => {
            if (!spaceAddress || !operatorAddress) {
                return
            }

            // Track delegation submission
            Analytics.getInstance().track('submitted delegation prompt', {
                spaceName: getSpaceNameFromCache(SpaceIdFromSpaceAddress(spaceAddress)),
                spaceId: spaceAddress,
                nodeName: operatorMetadata.name,
                nodeAddress: operatorAddress,
            })

            popupToast(
                ({ toast }) => (
                    <StandardToast.Pending toast={toast} message="Delegating to operator..." />
                ),
                {
                    id: 'tx',
                    duration: Number.POSITIVE_INFINITY,
                },
            )

            const signer = await getSigner()

            if (!signer) {
                return
            }

            addSpaceDelegation(
                {
                    signer,
                    operatorAddress,
                    spaceId: spaceAddress,
                },
                {
                    onSuccess: () => {
                        // Track delegation success
                        Analytics.getInstance().track('delegation prompt success', {
                            success: true,
                            spaceId: spaceAddress,
                            spaceName: getSpaceNameFromCache(SpaceIdFromSpaceAddress(spaceAddress)),
                            nodeName: operatorMetadata.name,
                            nodeAddress: operatorAddress,
                        })

                        popupToast(
                            ({ toast }) => (
                                <StandardToast.Success
                                    toast={toast}
                                    imgSrc={operatorMetadata.image}
                                    message={`Successfully delegated to ${operatorMetadata.name}`}
                                />
                            ),
                            { id: 'tx', duration: SECOND_MS * 3 },
                        )
                    },
                    onError: (error) => {
                        // Track delegation failure
                        Analytics.getInstance().track('delegation prompt success', {
                            success: false,
                            spaceId: spaceAddress,
                            spaceName: getSpaceNameFromCache(SpaceIdFromSpaceAddress(spaceAddress)),
                            nodeName: operatorMetadata.name,
                            nodeAddress: operatorAddress,
                            error: error?.message,
                        })

                        popupToast(
                            ({ toast }) => (
                                <StandardToast.Error
                                    toast={toast}
                                    message="Failed to delegate to node!"
                                    subMessage={error?.message}
                                />
                            ),
                            { id: 'tx', duration: SECOND_MS * 3 },
                        )
                    },
                },
            )
        },
        [addSpaceDelegation, operatorAddress, operatorMetadata, spaceAddress],
    )

    return { delegateToNode }
}
